// Security review: remount owner-scoped screens and dismiss stale toasts on session changes.
import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes } from 'react-router-dom';

import { WalletProvider, useWallet, WalletState } from './lib/wallet.jsx';
import { sessionBoundary } from './lib/session-boundary.js';
import { Button, Toast } from './components/ui.jsx';
import { Home } from './pages/Home.jsx';
import { MapMe } from './pages/MapMe.jsx';
import { DeployAgent } from './pages/DeployAgent.jsx';
import { WorkflowBuilder } from './pages/WorkflowBuilder.jsx';
import { Dashboard } from './pages/Dashboard.jsx';
import { Roster } from './pages/Roster.jsx';
import { Leaderboard } from './pages/Leaderboard.jsx';
import { AgentChat } from './pages/AgentChat.jsx';
import { Workflows } from './pages/Workflows.jsx';
import { Activity } from './pages/Activity.jsx';
import { Settings } from './pages/Settings.jsx';
import { RunDetail } from './pages/RunDetail.jsx';

import { CortisLogo } from './components/CortisLogo.jsx';

import '@cortis/design-tokens/css';
import './styles/app.css';
import './styles/proof-panel.css';
import './styles/leaderboard.css';

export default function App() {
  return (
    <WalletProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </WalletProvider>
  );
}

function Shell() {
  const { address, wrongNetwork, sessionEpoch } = useWallet();
  const [toasts, setToasts] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setToasts([]), [sessionEpoch]);

  const pushToast = useCallback((t) => {
    if (!sessionBoundary.current(sessionEpoch)) return;
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), 6000);
  }, [sessionEpoch]);
  const dismiss = useCallback((id) => setToasts((prev) => prev.filter((x) => x.id !== id)), []);

  // The app chrome and every screen render whether or not a wallet is
  // connected. Sign-in is a control in the top bar, not a wall in front of the
  // product: the interface is browsable, and it is the data-loading and the
  // actions that require a connected, signed-in wallet.

  return (
    <div className="shell">
      <a className="skip-link" href="#main">Skip to content</a>

      {navOpen && (
        <button
          type="button"
          className="sidebar__scrim"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
        />
      )}

      <aside className={`sidebar${navOpen ? ' sidebar--open' : ''}`}>
        <div className="sidebar__brand"><CortisLogo /></div>
        <nav className="sidebar__nav" aria-label="Main">
          {[
            ['/', 'Home'],
            ['/map', 'Map me'],
            ['/deploy', 'Deploy agent'],
            ['/build', 'Build workflow'],
            ['/operations', 'Operations'],
            ['/agents', 'Agents'],
            ['/leaderboard', 'Standings'],
            ['/workflows', 'Workflows'],
            ['/activity', 'Activity'],
            ['/settings', 'Settings'],
          ].map(([to, label]) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => `sidebar__link${isActive ? ' sidebar__link--active' : ''}`}
              onClick={() => setNavOpen(false)}
            >
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__foot">
          {address ? (
            <span className="mono sidebar__address">{address.slice(0, 6)}…{address.slice(-4)}</span>
          ) : (
            <span className="sidebar__address text-muted">Not connected</span>
          )}
        </div>
      </aside>

      <div className="shell__body">
        <header className="topbar">
          <button type="button" className="topbar__menu" onClick={() => setNavOpen((v) => !v)} aria-label="Toggle navigation" aria-expanded={navOpen}>
            ☰
          </button>
          {wrongNetwork && <span className="topbar__warning" role="alert">Wrong network — switch to opBNB</span>}
          <div className="topbar__spacer" />
          <ConnectControl />
        </header>

        <main id="main" className="main">
          <Routes key={sessionEpoch}>
            <Route path="/" element={<Home pushToast={pushToast} />} />
            <Route path="/map" element={<MapMe pushToast={pushToast} />} />
            <Route path="/deploy" element={<DeployAgent pushToast={pushToast} />} />
            <Route path="/build" element={<WorkflowBuilder pushToast={pushToast} />} />
            <Route path="/operations" element={<Dashboard />} />
            <Route path="/agents" element={<Roster pushToast={pushToast} />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/agents/:agentId" element={<AgentChat pushToast={pushToast} />} />
            <Route path="/workflows" element={<Workflows pushToast={pushToast} />} />
            <Route path="/activity" element={<Activity />} />
            <Route path="/runs/:runId" element={<RunDetail />} />
            <Route path="/settings" element={<Settings pushToast={pushToast} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>

      <Toast toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}

/**
 * The single sign-in affordance, top-right. It advances through the same wallet
 * state machine the old full-page SignIn used, but as a compact control that
 * never hides the app behind it.
 */
function ConnectControl() {
  const { state, connect, signIn, switchNetwork, isAuthenticated, address, error, signOut } = useWallet();

  if (isAuthenticated) {
    return (
      <div className="topbar__wallet" title={address ?? undefined}>
        <span className="topbar__dot" aria-hidden="true" />
        <span className="mono">{address ? `${address.slice(0, 6)}…${address.slice(-4)}` : 'Signed in'}</span>
        <button type="button" className="topbar__disconnect" onClick={() => signOut()}>Disconnect</button>
      </div>
    );
  }

  const view = {
    [WalletState.NO_PROVIDER]: { label: 'No wallet detected', onClick: null, loading: false, disabled: true },
    [WalletState.UNSUPPORTED_WALLET]: { label: 'Wallet unsupported', onClick: null, loading: false, disabled: true },
    [WalletState.CONNECTION_REJECTED]: { label: 'Retry connection', onClick: connect, loading: false },
    [WalletState.RPC_UNAVAILABLE]: { label: 'Retry connection', onClick: connect, loading: false },
    [WalletState.CONNECTING]: { label: 'Connecting', onClick: null, loading: true },
    [WalletState.CONNECTED]: { label: 'Sign in', onClick: signIn, loading: false },
    [WalletState.SIGNATURE_REQUIRED]: { label: 'Check wallet', onClick: null, loading: true },
    [WalletState.SIGNATURE_REJECTED]: { label: 'Sign in', onClick: signIn, loading: false },
    [WalletState.SESSION_EXPIRED]: { label: 'Sign in', onClick: signIn, loading: false },
    [WalletState.WRONG_NETWORK]: { label: 'Switch network', onClick: switchNetwork, loading: false },
    [WalletState.NETWORK_SWITCHING]: { label: 'Switching', onClick: null, loading: true },
    [WalletState.NETWORK_SWITCH_REJECTED]: { label: 'Switch network', onClick: switchNetwork, loading: false },
  }[state] ?? { label: 'Connect wallet', onClick: connect, loading: false };

  return (
    <div className="topbar__connect">
      <Button variant="primary" onClick={view.onClick ?? undefined} loading={view.loading} disabled={view.disabled}>
        {view.label}
      </Button>
      {error && <span className="topbar__connect-error" role="alert">{error}</span>}
    </div>
  );
}
